import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Space, message, Tabs, Divider, Row, Col, Checkbox } from 'antd';
import { User, Lock, Mail, Shield } from 'lucide-react';
import authService from '../services/authService';
import HelpPage from './HelpPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

interface AuthQQProps {
  onAuthSuccess: () => void;
}

const AuthQQ: React.FC<AuthQQProps> = ({ onAuthSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)').matches : false
  );
  const [activeTab, setActiveTab] = useState('login');
  const [currentPage, setCurrentPage] = useState<'auth' | 'help' | 'privacy' | 'terms'>('auth');
  const [registerForm] = Form.useForm();
  const [resetForm] = Form.useForm();

  // 验证码相关状态
  const [registerCodeSending, setRegisterCodeSending] = useState(false);
  const [registerCodeCountdown, setRegisterCodeCountdown] = useState(0);
  const [resetCodeSending, setResetCodeSending] = useState(false);
  const [resetCodeCountdown, setResetCodeCountdown] = useState(0);

  // 组件挂载后隐藏加载动画
  useEffect(() => {
    const timer = setTimeout(() => {
      // 优先使用强制隐藏函数
      if (typeof (window as any).forceHideLoading === 'function') {
        (window as any).forceHideLoading();
      } else if (typeof (window as any).hideLoadingAnimation === 'function') {
        (window as any).hideLoadingAnimation();
      } else {
        document.body.classList.add('app-loaded');
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // 跟随系统暗色模式切换，保持背景简约但自适应
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    try {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch {
      // Safari 旧版兼容
      // @ts-ignore
      mq.addListener(handler);
      return () => {
        // @ts-ignore
        mq.removeListener(handler);
      };
    }
  }, []);

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (registerCodeCountdown > 0) {
      timer = setTimeout(() => setRegisterCodeCountdown(registerCodeCountdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [registerCodeCountdown]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resetCodeCountdown > 0) {
      timer = setTimeout(() => setResetCodeCountdown(resetCodeCountdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resetCodeCountdown]);

  const handleLogin = async (values: any) => {
    try {
      setLoading(true);

      await authService.login(values.usernameOrEmail, values.password, values.rememberMe === true);

      // 若勾选记住我，保存用户名和密码；否则清除（兼容旧 rememberedUser）
      try {
        if (values.rememberMe) {
          const creds = {
            usernameOrEmail: values.usernameOrEmail || '',
            password: values.password || ''
          };
          localStorage.setItem('rememberedCreds', JSON.stringify(creds));
          // 迁移旧存储键
          localStorage.removeItem('rememberedUser');
        } else {
          localStorage.removeItem('rememberedCreds');
          localStorage.removeItem('rememberedUser');
        }
      } catch { }

      // 登录成功后，拉取一次用户信息，确保 user 持久化（写入 localStorage）
      try {
        await authService.refreshUserInfo();
      } catch { }

      message.success('登录成功');

      // iOS Safari可能需要额外的延迟来确保状态更新
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);
      if (isIOSSafari) {
        setTimeout(() => {
          onAuthSuccess();
        }, 100);
      } else {
        onAuthSuccess();
      }
    } catch (error: any) {
      let errorMessage = '登录失败，请检查用户名和密码';

      // 特殊处理iOS Safari的网络错误
      if (error.message?.includes('网络连接已断开')) {
        errorMessage = 'iOS设备网络检测异常，请确保网络连接正常后重试';
      } else if (error.response?.status === 200) {
        // 如果状态码是200但仍然进入catch，可能是iOS Safari的解析问题
        errorMessage = '登录可能已成功，请刷新页面重试';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.request && !error.response) {
        errorMessage = '网络请求超时，请检查网络连接后重试';
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: any) => {
    try {
      setLoading(true);

      // 先验证验证码
      const verifyResult = await authService.verifyCode(values.email, values.verificationCode, 'REGISTRATION');
      if (!verifyResult.success) {
        message.error(verifyResult.message || '验证码验证失败');
        return;
      }

      await authService.register(values.username, values.email, values.password);
      message.success('注册成功，请登录');
      setActiveTab('login');
      registerForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 发送注册验证码
  const handleSendRegisterCode = async () => {
    try {
      const email = registerForm.getFieldValue('email');
      if (!email) {
        message.error('请先输入邮箱地址');
        return;
      }

      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        message.error('请输入有效的邮箱地址');
        return;
      }

      setRegisterCodeSending(true);
      const result = await authService.sendVerificationCode(email, 'REGISTRATION');

      if (result.success) {
        message.success('验证码已发送到您的邮箱');
        setRegisterCodeCountdown(60);
      } else {
        message.error(result.message || '发送验证码失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '发送验证码失败，请重试');
    } finally {
      setRegisterCodeSending(false);
    }
  };

  // 处理找回密码
  const handleResetPassword = async (values: any) => {
    try {
      setLoading(true);

      await authService.resetPassword(values.email, values.verificationCode, values.newPassword);
      message.success('密码重置成功，请使用新密码登录');
      setActiveTab('login');
      resetForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码重置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 发送密码重置验证码
  const handleSendResetCode = async () => {
    try {
      const email = resetForm.getFieldValue('email');
      if (!email) {
        message.error('请先输入邮箱地址');
        return;
      }

      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        message.error('请输入有效的邮箱地址');
        return;
      }

      setResetCodeSending(true);
      const result = await authService.sendVerificationCode(email, 'PASSWORD_RESET');

      if (result.success) {
        message.success('验证码已发送到您的邮箱');
        setResetCodeCountdown(60);
      } else {
        message.error(result.message || '发送验证码失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '发送验证码失败，请重试');
    } finally {
      setResetCodeSending(false);
    }
  };

  // 如果当前页面不是认证页面，显示对应的页面
  if (currentPage === 'help') {
    return <HelpPage onBack={() => setCurrentPage('auth')} />;
  }

  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentPage('auth')} />;
  }

  if (currentPage === 'terms') {
    return <TermsOfService onBack={() => setCurrentPage('auth')} />;
  }

  // 从本地读取记住的账号密码（兼容旧 rememberedUser）
  let initialUsername = '';
  let initialPassword = '';
  let initialRemember = false;
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('rememberedCreds') : null;
    if (raw) {
      const saved = JSON.parse(raw);
      initialUsername = saved?.usernameOrEmail || '';
      initialPassword = saved?.password || '';
      initialRemember = true;
    } else {
      const legacy = typeof window !== 'undefined' ? localStorage.getItem('rememberedUser') : '';
      if (legacy) {
        initialUsername = legacy;
        initialRemember = true;
      }
    }
  } catch { }

  return (
    <div style={{
      minHeight: '100vh',
      background: isDarkMode ? '#0f0f0f' : '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          background: isDarkMode ? '#1f1f1f' : '#ffffff'
        }}
        styles={{ body: { padding: '32px' } }}
      >
        {/* Logo和标题 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
              overflow: 'hidden'
            }}>
              <img
                src="/icons/icon-512.png"
                alt="小酷笔记"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              小酷笔记
            </Title>
          </div>
          <Text type="secondary">智能笔记应用 - 网页版</Text>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          style={{ marginBottom: 0 }}
        >
          <TabPane tab="登录" key="login">
            <Form
              name="login"
              onFinish={handleLogin}
              layout="vertical"
              size="large"
              initialValues={{
                usernameOrEmail: initialUsername,
                password: initialPassword,
                rememberMe: initialRemember
              }}
            >
              <Form.Item
                name="usernameOrEmail"
                rules={[{ required: true, message: '请输入用户名或邮箱' }]}
              >
                <Input
                  prefix={<User size={16} />}
                  placeholder="用户名或邮箱"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<Lock size={16} />}
                  placeholder="密码"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item name="rememberMe" valuePropName="checked" style={{ marginBottom: 8 }}>
                <Checkbox>记住我</Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ height: 44 }}
                >
                  登录
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center' }}>
                <Link
                  onClick={() => setActiveTab('reset')}
                  style={{ fontSize: '14px' }}
                >
                  忘记密码？
                </Link>
              </div>
            </Form>
          </TabPane>

          <TabPane tab="注册" key="register">
            <Form
              form={registerForm}
              name="register"
              onFinish={handleRegister}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { max: 20, message: '用户名最多20个字符' }
                ]}
              >
                <Input
                  prefix={<User size={16} />}
                  placeholder="用户名"
                />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<Mail size={16} />}
                  placeholder="邮箱"
                />
              </Form.Item>

              <Form.Item
                name="verificationCode"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 6, message: '验证码为6位数字' }
                ]}
              >
                <Row gutter={8}>
                  <Col span={14}>
                    <Input
                      prefix={<Shield size={16} />}
                      placeholder="邮箱验证码"
                      maxLength={6}
                    />
                  </Col>
                  <Col span={10}>
                    <Button
                      block
                      loading={registerCodeSending}
                      disabled={registerCodeCountdown > 0}
                      onClick={handleSendRegisterCode}
                      style={{ height: '40px' }}
                    >
                      {registerCodeCountdown > 0 ? `${registerCodeCountdown}s` : '获取验证码'}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<Lock size={16} />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<Lock size={16} />}
                  placeholder="确认密码"
                />
              </Form.Item>

              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('请同意隐私政策和服务条款')),
                  },
                ]}
                style={{ marginBottom: 16 }}
              >
                <Checkbox>
                  我已阅读并同意{' '}
                  <Typography.Link
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage('privacy');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    隐私政策
                  </Typography.Link>
                  {' '}和{' '}
                  <Typography.Link
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage('terms');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    服务条款
                  </Typography.Link>
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ height: 44 }}
                >
                  注册
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="找回密码" key="reset">
            <Form
              form={resetForm}
              name="reset"
              onFinish={handleResetPassword}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<Mail size={16} />}
                  placeholder="注册时使用的邮箱"
                />
              </Form.Item>

              <Form.Item
                name="verificationCode"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 6, message: '验证码为6位数字' }
                ]}
              >
                <Row gutter={8}>
                  <Col span={14}>
                    <Input
                      prefix={<Shield size={16} />}
                      placeholder="邮箱验证码"
                      maxLength={6}
                    />
                  </Col>
                  <Col span={10}>
                    <Button
                      block
                      loading={resetCodeSending}
                      disabled={resetCodeCountdown > 0}
                      onClick={handleSendResetCode}
                      style={{ height: '40px' }}
                    >
                      {resetCodeCountdown > 0 ? `${resetCodeCountdown}s` : '获取验证码'}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item
                name="newPassword"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<Lock size={16} />}
                  placeholder="新密码"
                />
              </Form.Item>

              <Form.Item
                name="confirmNewPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<Lock size={16} />}
                  placeholder="确认新密码"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ height: 44 }}
                >
                  重置密码
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>

        <Divider />

        {/* 底部信息 */}
        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="small">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              网页版支持离线编辑和云端同步
            </Text>
            <Space split={<span style={{ color: '#d9d9d9' }}>•</span>}>
              <Link onClick={() => setCurrentPage('help')} style={{ fontSize: '12px', cursor: 'pointer' }}>使用帮助</Link>
              <Link onClick={() => setCurrentPage('privacy')} style={{ fontSize: '12px', cursor: 'pointer' }}>隐私政策</Link>
              <Link onClick={() => setCurrentPage('terms')} style={{ fontSize: '12px', cursor: 'pointer' }}>服务条款</Link>
            </Space>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default AuthQQ;
